import { FC, useReducer } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { Button, TextField, FormControl, Select, MenuItem, FormHelperText } from '@mui/material';
import { useErrorHandler, ErrorFieldType } from 'src/app/common/utils';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';
import { CRITERIA_PATH, SIMULATION_TOOL_PATH } from 'src/app/modules/SimulationTool/constants';
import {
  CriteriaFormMode,
  CriteriaListItem,
  CriteriaTypeEnum,
} from 'src/app/modules/SimulationTool/types/criteria-setting';
import {
  createCriteriaItem,
  CreateUpdateCriteriaBody,
  updateCriteriaItem,
} from 'src/app/modules/SimulationTool/network/criteriaCrud';

type CriteriaFormProps = {
  formMode: CriteriaFormMode;
  criteriaItem?: CriteriaListItem;
};

type CriteriaFormState = {
  name?: string;
  type?: CriteriaTypeEnum;
};

type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof CriteriaFormState;
    value: any;
  };
};

type CriteriaFormAction = ModifyFieldAction;

const formReducer = (state: CriteriaFormState, action: CriteriaFormAction): CriteriaFormState => {
  switch (action.type) {
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    default:
      return state;
  }
};

const detailToStateConvertor = (criteriaItem?: CriteriaListItem): CriteriaFormState => {
  if (criteriaItem) {
    return {
      name: criteriaItem.name,
      type: criteriaItem.type
    };
  } else {
    return {
      name: undefined,
      type: undefined,
    };
  }
};

const FIELD_CONTAINER_WIDTH = 160;
const FIELD_WIDTH = 200;

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  rowSpacing: {
    marginBottom: 16,
  },
  fieldContainer: {
    width: FIELD_CONTAINER_WIDTH,
    boxSizing: 'border-box',
  },
  field: {
    fontSize: '1rem',
  },
  fieldBox: {
    minWidth: FIELD_WIDTH,
  },
  noMargin: {
    margin: 0,
  },
  mandatory: {
    color: 'red',
  },
  sectionMargin: {
    marginBottom: 15,
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
}));

const CriteriaSettingForm: FC<CriteriaFormProps> = ({ formMode, criteriaItem }) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const intl = useIntl();
  const Translation = (id: string, variable?: Record<string, string>) => intl.formatMessage({ id }, variable);
  const dispatch = useDispatch();
  const history = useHistory();

  const [formState, formDispatch] = useReducer(formReducer, detailToStateConvertor(criteriaItem));

  const { errorState, onSubmitErrorValidator, onDismissErrorHandler } = useErrorHandler(formState, [
    {
      name: 'name',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'type',
      fieldType: ErrorFieldType.MANDATORY,
    },
  ]);

  const onSubmit = async () => {
    const { hasError } = onSubmitErrorValidator();
    if (!hasError) {
      const body: CreateUpdateCriteriaBody = {
        name: formState.name || '',
        type: formState.type as CriteriaTypeEnum,
      };
      try {
        if (formMode === CriteriaFormMode.CREATE) {
          await createCriteriaItem(body, dispatch);
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: 'Criteria saved successfully',
              },
            ]),
          );
        } else if (criteriaItem) {
          await updateCriteriaItem(criteriaItem._id, body, dispatch);
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: `Criteria updated successfully`,
              },
            ]),
          );
        }
        history.push(`${SIMULATION_TOOL_PATH}${CRITERIA_PATH}`);
      } catch (err) {}
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.rowContainer}>
          <div className={commonClasses.header}>
            {Translation(`criteriaSetting.form.title.${formMode === CriteriaFormMode.CREATE ? 'create' : 'edit'}`)}
          </div>
        </div>
        <Button
          variant="contained"
          color="inherit"
          onClick={() => history.push(`${SIMULATION_TOOL_PATH}${CRITERIA_PATH}`)}
        >
          {Translation('app.button.back')}
        </Button>
      </div>
      <div className={classes.sectionMargin}>
        <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('criteriaSetting.name')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextField
              error={errorState.mandatory.name}
              className={`${classes.fieldBox} ${classes.noMargin}`}
              margin="dense"
              variant="outlined"
              fullWidth
              helperText={errorState.mandatory.name && MANDATORY_FIELD_ERROR_TEXT}
              value={formState.name || ''}
              onChange={(e) => {
                onDismissErrorHandler('name', e.target.value);
                formDispatch({
                  type: 'MODIFY_FIELD',
                  payload: { field: 'name', value: e.target.value },
                });
              }}
            />
          </div>
        </div>
        <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('criteriaSetting.type')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <FormControl
              disabled={criteriaItem?.isExistParameter}
              error={errorState.mandatory.type}
              className={`${classes.fieldBox} ${classes.noMargin}`}
              margin="dense"
              variant="outlined"
              fullWidth
            >
              <Select
                value={formState.type || ''}
                onChange={(e) => {
                  onDismissErrorHandler('type', e.target.value);
                  formDispatch({
                    type: 'MODIFY_FIELD',
                    payload: { field: 'type', value: e.target.value },
                  });
                }}
              >
                {Object.values(CriteriaTypeEnum).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              {errorState.mandatory.type && (
                <FormHelperText style={{ color: '#f018a6' }}>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
              )}
            </FormControl>
          </div>
        </div>
      </div>
      <div className={classes.footerContainer}>
        <Button variant="contained" color="secondary" onClick={onSubmit}>
          {Translation('app.button.submit')}
        </Button>
      </div>
    </div>
  );
};

export default CriteriaSettingForm;
